/**
 * Weekly opening hours (#2465, split out of the 991-line workshop form).
 *
 * A day with `null` hours is closed; toggling it on restores the workshop's
 * usual 08:00-17:00 rather than leaving empty time fields.
 */

import { Box, Paper, Typography, TextField, Divider, FormControlLabel, Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { WorkshopSettings, WorkshopOpeningHours } from '@/types';

const dayKeys: (keyof WorkshopOpeningHours)[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];


interface Props {
  settings: WorkshopSettings;
  onChange: (settings: WorkshopSettings) => void;
}

export function WorkshopSectionHours({ settings, onChange }: Props) {
  const { t } = useTranslation('settings');

  const handleHoursChange = (
    day: keyof WorkshopOpeningHours,
    field: 'open' | 'close',
    value: string
  ) => {
    const currentDay = settings.openingHours[day];
    onChange({
      ...settings,
      openingHours: {
        ...settings.openingHours,
        [day]: currentDay ? { ...currentDay, [field]: value } : { open: '08:00', close: '17:00', [field]: value },
      },
    });
  };

  const handleDayToggle = (day: keyof WorkshopOpeningHours, enabled: boolean) => {
    onChange({
      ...settings,
      openingHours: {
        ...settings.openingHours,
        [day]: enabled ? { open: '08:00', close: '17:00' } : null,
      },
    });
  };

  // Exception handlers. Exceptions are their own resource (#1282), so each edit
  // persists immediately instead of riding along with the form's Save button.


  return (
    <>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('openingHours')}
      </Typography>

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
        {dayKeys.map((day, index) => {
          const hours = settings.openingHours[day];
          const isOpen = hours !== null;

          return (
            <Box key={day}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  py: 1.5,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={isOpen}
                      onChange={(e) => handleDayToggle(day, e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ width: 100 }}>
                      {t(`days.${day}`)}
                    </Typography>
                  }
                  sx={{ m: 0, minWidth: 160 }}
                />

                {isOpen && hours && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      size="small"
                      type="time"
                      value={hours.open}
                      onChange={(e) => handleHoursChange(day, 'open', e.target.value)}
                      sx={{ width: 120 }}
                    />
                    <Typography color="text.secondary">-</Typography>
                    <TextField
                      size="small"
                      type="time"
                      value={hours.close}
                      onChange={(e) => handleHoursChange(day, 'close', e.target.value)}
                      sx={{ width: 120 }}
                    />
                  </Box>
                )}

                {!isOpen && (
                  <Typography variant="body2" color="text.secondary">
                    {t('closed')}
                  </Typography>
                )}
              </Box>
              {index < dayKeys.length - 1 && <Divider />}
            </Box>
          );
        })}
      </Paper>
    </>
  );
}
